import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Coins, AlertTriangle, RefreshCw, Download, AlertCircle, Plug, Wrench, CheckCircle2
} from 'lucide-react';

const todayISO = () => new Date().toISOString().split('T')[0];
const daysAgoISO = (n) => new Date(Date.now() - n * 86400_000).toISOString().split('T')[0];
const fmtR = (n) => {
  if (n == null) return '-';
  const v = Number(n);
  const sign = v < 0 ? '-' : '';
  return `${sign}R${Math.abs(v).toFixed(2)}`;
};
const colorByValue = (v) => (Number(v) < 0 ? 'text-rose-700' : 'text-stone-800');

export function GratuitiesTab() {
  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Missing-branch diagnostic
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  // Straggler fixer (manual per-record branch assign)
  const [stragglersOpen, setStragglersOpen] = useState(false);
  const [stragglersLoading, setStragglersLoading] = useState(false);
  const [stragglers, setStragglers] = useState([]);
  const [selectedStragglers, setSelectedStragglers] = useState({});
  const [bulkBranchId, setBulkBranchId] = useState('');
  const [savingStragglers, setSavingStragglers] = useState(false);
  const [branches, setBranches] = useState([]);

  const loadBranches = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/branches`);
      const list = Array.isArray(r.data) ? r.data : (r.data?.branches || []);
      setBranches(list);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadBranches(); }, [loadBranches]);

  const openStragglers = useCallback(async () => {
    setStragglersOpen(true);
    setStragglersLoading(true);
    setStragglers([]);
    setSelectedStragglers({});
    setBulkBranchId('');
    try {
      const r = await axios.get(`${API}/attendance/missing-branch`, {
        params: { date_from: dateFrom, date_to: dateTo },
        timeout: 2 * 60 * 1000,
      });
      const recs = r.data?.records || [];
      setStragglers(recs);
      // pre-select all
      const sel = {};
      recs.forEach(r => { sel[r.id] = true; });
      setSelectedStragglers(sel);
      if (recs.length === 0) {
        toast.info('No missing-branch records in this range.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
      setStragglersOpen(false);
    } finally {
      setStragglersLoading(false);
    }
  }, [dateFrom, dateTo]);

  const saveStragglers = async () => {
    if (!bulkBranchId) { toast.error('Pick a branch to assign.'); return; }
    const ids = Object.entries(selectedStragglers)
      .filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error('Select at least one record.'); return; }
    setSavingStragglers(true);
    try {
      const r = await axios.post(`${API}/attendance/assign-branch-bulk`, {
        record_ids: ids, branch_id: bulkBranchId,
      });
      const d = r.data;
      toast.success(
        `Updated ${d.updated} · already set ${d.already_set} · not found ${d.not_found} → ${d.branch_name}`
      );
      setStragglersOpen(false);
      // Re-run the gratuities report so the diagnostic banner refreshes
      // (the "N of M records missing a branch" warning + the action buttons
      // that trigger this modal live off of report.errors).
      if (d.updated > 0) loadReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setSavingStragglers(false);
    }
  };

  const diagnoseMissingBranches = useCallback(async () => {
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const r = await axios.post(`${API}/timetec/diagnose-missing-branches`, null, {
        params: { start_date: dateFrom, end_date: dateTo },
        timeout: 5 * 60 * 1000,
      });
      setDiagResult(r.data);
      if (r.data.shells_inserted > 0) {
        toast.success(`Discovered ${r.data.shells_inserted} new device(s) — now visible under TimeTec Clocking Devices.`);
      } else if (r.data.resolved_devices?.length) {
        toast.success(`Identified ${r.data.resolved_devices.length} device(s) needing a branch mapping.`);
      } else {
        toast.info('No unmapped devices found for this range.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setDiagLoading(false);
    }
  }, [dateFrom, dateTo]);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/gratuities/status`);
      setStatus(r.data);
    } catch (e) { /* silent */ }
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gratuities/report`, { params: { start_date: dateFrom, end_date: dateTo } });
      setReport(r.data);
      if (r.data.errors?.length) r.data.errors.forEach(err => toast.error(err));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const downloadXlsx = () => {
    if (!report) return;
    const round2 = (v) => Number((Number(v) || 0).toFixed(2));
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Per-employee totals (the most useful sheet for payroll)
    const totalsHeader = ['SimplePay Code', 'Employee', 'Total Hours', 'Total Gratuity (R)'];
    const totalsRows = (report.totals_by_employee || []).map(t => [
      t.simplepay_code || '',
      t.employee_name,
      round2(t.total_hours),
      round2(t.total_amount),
    ]);
    const wsTotals = XLSX.utils.aoa_to_sheet([
      [`Gratuities Totals · ${dateFrom} → ${dateTo}`],
      [],
      totalsHeader,
      ...totalsRows,
    ]);
    wsTotals['!cols'] = totalsHeader.map((h) => ({
      wch: Math.max(12, Math.min(32, h.length + 4))
    }));
    XLSX.utils.book_append_sheet(wb, wsTotals, 'Totals by Employee');

    // Sheet 2 — Detail per day per branch per employee
    const detailHeader = [
      'Date', 'Branch', 'Cash Recon Branch', 'SimplePay Code', 'Employee',
      'Hours', 'Cash Tips', 'Card Tips', 'Total Tips',
      'Branch Total Hours', 'Portion (R)'
    ];
    const detailRows = [];
    (report.rows || []).forEach(row => {
      if (!row.employees || row.employees.length === 0) {
        detailRows.push([
          row.date, row.branch_name, row.cash_branch_name || '', '', '',
          0, round2(row.cash_tips), round2(row.card_tips), round2(row.total_tips),
          round2(row.total_hours), 0
        ]);
      } else {
        row.employees.forEach(e => {
          detailRows.push([
            row.date,
            row.branch_name,
            row.cash_branch_name || '',
            e.simplepay_code || '',
            e.employee_name,
            round2(e.hours),
            round2(row.cash_tips),
            round2(row.card_tips),
            round2(row.total_tips),
            round2(row.total_hours),
            round2(e.amount),
          ]);
        });
      }
    });
    const wsDetail = XLSX.utils.aoa_to_sheet([detailHeader, ...detailRows]);
    wsDetail['!cols'] = detailHeader.map((h) => ({
      wch: Math.max(12, Math.min(32, h.length + 4))
    }));
    XLSX.utils.book_append_sheet(wb, wsDetail, 'Detail');

    // Sheet 3 — Unmatched branches (warnings) if any
    if (report.unmatched_branches && report.unmatched_branches.length > 0) {
      const wsUnmatched = XLSX.utils.aoa_to_sheet([
        ['Date', 'Cash Recon Branch Name (unmatched)'],
        ...report.unmatched_branches.map(b => [b.date, b.branch_name]),
      ]);
      wsUnmatched['!cols'] = [{ wch: 14 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, wsUnmatched, 'Unmatched Branches');
    }

    XLSX.writeFile(wb, `gratuities_${dateFrom}_${dateTo}.xlsx`);
  };

  return (
    <div className="space-y-4" data-testid="gratuities-tab">
      <div className="bg-white rounded-xl border border-stone-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <Coins className="w-5 h-5" /> Gratuities
            </h2>
            <p className="text-xs text-stone-500 mt-1">
              Apportions reconciled cash + card tips from the Cash Recon app to tip-eligible staff based on approved hours worked per branch.
            </p>
          </div>
          {status && !status.configured && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800 flex items-center gap-2" data-testid="cash-manager-not-configured">
              <Plug className="w-4 h-4" />
              Cash Manager is not connected. Go to <b>Settings → Integrations</b>.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 p-3 flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            data-testid="grat-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            data-testid="grat-date-to" />
        </div>
        <Button onClick={loadReport} disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="grat-load-button">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Load report'}
        </Button>
        {report && (
          <Button variant="outline" onClick={downloadXlsx} data-testid="grat-download-button">
            <Download className="w-4 h-4 mr-1" /> Download Excel (.xlsx)
          </Button>
        )}
      </div>

      {/* Diagnostics — upstream/business-filter visibility */}
      {report && report.diagnostics && (() => {
        const diag = report.diagnostics;
        const expected = (diag.expected_business_name || '').trim().toLowerCase();
        const received = (diag.received_business_names || []).map(n => (n || '').trim().toLowerCase());
        const expectedInReceived = !!expected && received.includes(expected);

        if (diag.upstream_rows_count === 0) {
          return (
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-sm text-stone-700"
              data-testid="diag-no-upstream">
              <div className="font-medium flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> No tips returned by Cash Recon for this range
              </div>
              <div className="text-xs mt-1">
                The Cash Manager API returned 0 rows for {dateFrom} → {dateTo}.
                Check that recon entries exist in the Cash Manager app for these dates.
              </div>
            </div>
          );
        }
        if (diag.business_filtered_out > 0 && !expectedInReceived) {
          return (
            <div className="bg-rose-50 border border-rose-300 rounded-xl p-3 text-sm text-rose-900"
              data-testid="diag-business-mismatch">
              <div className="font-medium flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Business name mismatch — {diag.business_filtered_out} upstream row(s) discarded
              </div>
              <div className="text-xs mt-1">
                Configured: <b>{diag.expected_business_name || '<empty>'}</b>.
                Cash Recon only sent rows for: <b>{diag.received_business_names.join(', ') || '<none>'}</b>.
                <br />Fix this under <b>Settings → Integrations → Cash Manager → Business Name</b> — it must match exactly (case-insensitive, no extra spaces).
              </div>
            </div>
          );
        }
        if (diag.business_filtered_out > 0) {
          return (
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs text-stone-600"
              data-testid="diag-business-partial">
              Note: {diag.business_filtered_out} upstream row(s) for other businesses were discarded
              (kept: <b>{diag.expected_business_name}</b>).
            </div>
          );
        }
        return null;
      })()}

      {/* Pipeline diagnostics — funnel counts so admin can see WHERE hours drop off */}
      {report && report.diagnostics && report.diagnostics.eligible_users_count !== undefined && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-3" data-testid="diag-pipeline">
          <div className="text-xs font-semibold text-stone-700 mb-2">Apportionment pipeline</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="bg-white rounded p-2 border border-stone-100">
              <div className="text-stone-500">Tip-eligible staff</div>
              <div className={`font-mono text-lg ${report.diagnostics.eligible_users_count === 0 ? 'text-rose-700' : 'text-stone-800'}`}
                data-testid="diag-eligible-users">{report.diagnostics.eligible_users_count}</div>
            </div>
            <div className="bg-white rounded p-2 border border-stone-100">
              <div className="text-stone-500">Records for them</div>
              <div className="font-mono text-lg text-stone-800"
                data-testid="diag-att-total">{report.diagnostics.attendance_eligible_total ?? 0}</div>
            </div>
            <div className="bg-white rounded p-2 border border-stone-100">
              <div className="text-stone-500">Fully signed off</div>
              <div className={`font-mono text-lg ${
                report.diagnostics.attendance_fully_approved === 0 &&
                report.diagnostics.attendance_eligible_total > 0
                  ? 'text-rose-700' : 'text-stone-800'
              }`} data-testid="diag-att-approved">{report.diagnostics.attendance_fully_approved ?? 0}</div>
            </div>
            <div className="bg-white rounded p-2 border border-stone-100">
              <div className="text-stone-500">With positive hours</div>
              <div className="font-mono text-lg text-stone-800"
                data-testid="diag-att-hours">{report.diagnostics.attendance_with_positive_hours ?? 0}</div>
            </div>
            <div className="bg-white rounded p-2 border border-stone-100">
              <div className="text-stone-500">With branch resolved</div>
              <div className={`font-mono text-lg ${
                report.diagnostics.attendance_with_positive_hours > 0 &&
                report.diagnostics.attendance_with_branch_set <
                  report.diagnostics.attendance_with_positive_hours
                  ? 'text-amber-700' : 'text-stone-800'
              }`} data-testid="diag-att-branched">{report.diagnostics.attendance_with_branch_set ?? 0}</div>
            </div>
          </div>
          {Array.isArray(report.diagnostics.excluded_users) && report.diagnostics.excluded_users.length > 0 && (
            <details className="mt-3 text-xs" data-testid="diag-excluded-users">
              <summary className="cursor-pointer text-stone-600 hover:text-stone-800">
                {report.diagnostics.excluded_users.length} user{report.diagnostics.excluded_users.length === 1 ? '' : 's'} excluded from this tip pool
              </summary>
              <ul className="mt-2 space-y-1 ml-4 list-disc text-stone-700">
                {report.diagnostics.excluded_users.map((u, i) => (
                  <li key={i}>
                    <b>{u.display_name || u.username}</b>
                    <span className="text-stone-500"> — {u.role}, employee_code: {u.employee_code || '—'}, toggle: {String(u.include_in_gratuities)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-stone-500">
                If a user you expected to see here is <b>missing</b>, their &quot;Include in Gratuities&quot; toggle is still ON — flip it OFF under Settings → Users → Edit.
              </div>
            </details>
          )}
        </div>
      )}

      {/* Unmatched branches warning */}
      {report && report.unmatched_branches?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="unmatched-branches-banner">
          <div className="font-medium text-amber-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {report.unmatched_branches.length} unmatched branch entries
          </div>
          <div className="text-xs text-amber-800 mt-1">
            These branches were returned by the Cash Recon app but don&apos;t match any local branch. Add the exact name to a branch under <b>Settings → Branches → Cash Manager Branch Name</b>.
          </div>
          <ul className="text-xs text-amber-900 mt-2 list-disc ml-6">
            {report.unmatched_branches.slice(0, 10).map((b, i) => (
              <li key={i}><b>{b.branch_name}</b> on {b.date}{b.total_tips ? ` — R${Number(b.total_tips).toFixed(2)}` : ''}</li>
            ))}
            {report.unmatched_branches.length > 10 && <li>+{report.unmatched_branches.length - 10} more…</li>}
          </ul>
        </div>
      )}

      {/* Tips reconciliation - exposes the exact gap between what Cash
          Recon sent and what got apportioned. */}
      {report && report.diagnostics?.tips_reconciliation && (() => {
        const tr = report.diagnostics.tips_reconciliation;
        const gap = Number(tr.total_tips_gap) || 0;
        const hasGap = Math.abs(gap) >= 0.01;
        return (
          <div className={`rounded-xl p-3 border ${hasGap ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}
            data-testid="tips-reconciliation">
            <div className={`font-medium flex items-center gap-2 ${hasGap ? 'text-amber-900' : 'text-emerald-900'}`}>
              {hasGap ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              Tips reconciliation
              {hasGap && <span className="text-xs font-normal">— R{gap.toFixed(2)} not apportioned</span>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
              <div className="bg-white rounded p-2 border border-stone-100">
                <div className="text-stone-500">Received from Cash Recon</div>
                <div className="font-mono text-lg text-stone-800" data-testid="tr-received">R{Number(tr.total_tips_received).toFixed(2)}</div>
              </div>
              <div className="bg-white rounded p-2 border border-stone-100">
                <div className="text-stone-500">Apportioned to staff</div>
                <div className="font-mono text-lg text-stone-800" data-testid="tr-apportioned">R{Number(tr.total_tips_apportioned).toFixed(2)}</div>
              </div>
              <div className="bg-white rounded p-2 border border-stone-100">
                <div className="text-stone-500">Unmatched branch{tr.unmatched_branch_count ? ` (${tr.unmatched_branch_count} row${tr.unmatched_branch_count === 1 ? '' : 's'})` : ''}</div>
                <div className={`font-mono text-lg ${Number(tr.total_tips_unmatched_branch) > 0 ? 'text-amber-700' : 'text-stone-800'}`}
                  data-testid="tr-unmatched">R{Number(tr.total_tips_unmatched_branch).toFixed(2)}</div>
              </div>
              <div className="bg-white rounded p-2 border border-stone-100">
                <div className="text-stone-500">No signed-off hours{tr.dropped_no_hours_count ? ` (${tr.dropped_no_hours_count} row${tr.dropped_no_hours_count === 1 ? '' : 's'})` : ''}</div>
                <div className={`font-mono text-lg ${Number(tr.total_tips_no_eligible_hours) > 0 ? 'text-amber-700' : 'text-stone-800'}`}
                  data-testid="tr-no-hours">R{Number(tr.total_tips_no_eligible_hours).toFixed(2)}</div>
              </div>
            </div>
            {Math.abs(Number(tr.total_tips_rounding_residual)) >= 0.01 && (
              <div className="text-xs text-stone-500 mt-2">
                Rounding residual: R{Number(tr.total_tips_rounding_residual).toFixed(2)} (per-employee amounts rounded to 2dp).
              </div>
            )}
            {hasGap && (
              <div className="text-xs text-amber-900 mt-2">
                <b>How to close the gap:</b>{' '}
                {Number(tr.total_tips_unmatched_branch) > 0 && 'Map any unmatched branches (see the amber panel above). '}
                {Number(tr.total_tips_no_eligible_hours) > 0 && 'Sign-off the missing attendance for the day/branch below - staff need both employee + manager sign-off (or admin override). '}
                {Math.abs(Number(tr.total_tips_rounding_residual)) >= 0.01 && 'The rounding residual is a few cents per row from 2dp rounding and is normally acceptable.'}
              </div>
            )}
          </div>
        );
      })()}

      {/* Tip rows dropped because no signed-off attendance for that day/branch */}
      {report && report.dropped_no_hours?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="dropped-no-hours-banner">
          <div className="font-medium text-amber-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {report.dropped_no_hours.length} tip row{report.dropped_no_hours.length === 1 ? '' : 's'} with no eligible attendance
          </div>
          <div className="text-xs text-amber-800 mt-1">
            The Cash Recon app returned tips for these (date, branch) pairs but no staff have <b>signed-off</b> attendance with a resolved branch. Sign-off the missing shifts (or admin-override them) to include these tips in the apportionment.
          </div>
          <ul className="text-xs text-amber-900 mt-2 list-disc ml-6">
            {report.dropped_no_hours.slice(0, 15).map((b, i) => (
              <li key={i}><b>{b.branch_name}</b> on {b.date} — R{Number(b.total_tips).toFixed(2)}</li>
            ))}
            {report.dropped_no_hours.length > 15 && <li>+{report.dropped_no_hours.length - 15} more…</li>}
          </ul>
        </div>
      )}

      {/* Errors */}
      {report && report.errors?.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-800">
          {report.errors.map((e, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                {e}
                {/missing a branch|unmapped device|no resolved branch/i.test(e) && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button variant="outline" size="sm" onClick={diagnoseMissingBranches}
                      disabled={diagLoading}
                      data-testid="diagnose-missing-branches-button">
                      <Plug className={`w-3 h-3 mr-1 ${diagLoading ? 'animate-spin' : ''}`} />
                      {diagLoading ? 'Diagnosing…' : 'Identify unmapped devices'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={openStragglers}
                      disabled={stragglersLoading}
                      data-testid="assign-stragglers-button">
                      <Wrench className={`w-3 h-3 mr-1 ${stragglersLoading ? 'animate-spin' : ''}`} />
                      {stragglersLoading ? 'Loading…' : 'Fix stragglers manually'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Missing-branch diagnostic result */}
      {diagResult && (
        <div className="bg-white border border-amber-300 rounded-xl overflow-hidden" data-testid="diagnose-result-panel">
          <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
            <div className="font-semibold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Unmapped device diagnostic
            </div>
            <div className="text-xs text-amber-800 mt-1">
              <b>{diagResult.total_missing}</b> approved record(s) with no branch in this range.
              {diagResult.shells_inserted > 0 && (
                <> {diagResult.shells_inserted} previously unknown device(s) were auto-added to the Devices list.</>
              )}
              {diagResult.unresolved_count > 0 && (
                <> {diagResult.unresolved_count} record(s) couldn&apos;t be tied to any device in TimeTec&apos;s audit log.</>
              )}
            </div>
          </div>
          {diagResult.resolved_devices?.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-700">
                <tr>
                  <th className="text-left px-4 py-2">Terminal SN</th>
                  <th className="text-right px-4 py-2">Records affected</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Sample dates</th>
                  <th className="text-left px-4 py-2">Sample staff</th>
                </tr>
              </thead>
              <tbody>
                {diagResult.resolved_devices.map((d, i) => (
                  <tr key={i} className="border-t border-stone-100"
                    data-testid={`unmapped-device-row-${d.terminal_sn}`}>
                    <td className="px-4 py-2 font-mono">{d.terminal_sn}</td>
                    <td className="px-4 py-2 text-right font-mono">{d.count}</td>
                    <td className="px-4 py-2">
                      {d.mapped ? (
                        <span className="text-emerald-700">Mapped → {d.branch_name}</span>
                      ) : (
                        <span className="text-rose-700 font-semibold">Unmapped — needs branch</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-stone-600">
                      {(d.sample_dates || []).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-stone-600">
                      {(d.sample_staff || []).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-6 text-sm text-stone-600">No unmapped devices found.</div>
          )}
          <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-3 text-sm text-emerald-900">
            <b>Next step:</b> {diagResult.recommendation}
          </div>
        </div>
      )}

      {/* Per-employee totals */}
      {report && report.totals_by_employee?.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-stone-200 font-medium text-sm">
            Per-employee totals · {dateFrom} → {dateTo}
          </div>
          <table className="w-full text-sm" data-testid="employee-totals-table">
            <thead className="bg-stone-100 text-stone-700">
              <tr>
                <th className="text-left px-3 py-2">SimplePay Code</th>
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-right px-3 py-2">Total Hours</th>
                <th className="text-right px-3 py-2">Total Gratuity</th>
              </tr>
            </thead>
            <tbody>
              {report.totals_by_employee.map(t => (
                <tr key={t.employee_id} className="border-t border-stone-100">
                  <td className="px-3 py-2 font-mono text-stone-600">{t.simplepay_code || '-'}</td>
                  <td className="px-3 py-2">{t.employee_name}</td>
                  <td className="px-3 py-2 text-right font-mono">{t.total_hours.toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${colorByValue(t.total_amount)}`}>
                    {fmtR(t.total_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-day breakdown */}
      {report && report.rows?.length > 0 && (
        <div className="space-y-3">
          {report.rows.map((row, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold text-stone-800">{row.date} · {row.branch_name}</div>
                  {row.cash_branch_name !== row.branch_name && (
                    <div className="text-xs text-stone-500">Cash Recon name: <code>{row.cash_branch_name}</code></div>
                  )}
                </div>
                <div className="text-xs text-stone-600 flex gap-4">
                  <span>Cash: <b className={colorByValue(row.cash_tips)}>{fmtR(row.cash_tips)}</b></span>
                  <span>Card: <b className={colorByValue(row.card_tips)}>{fmtR(row.card_tips)}</b></span>
                  <span>Total: <b className={colorByValue(row.total_tips)}>{fmtR(row.total_tips)}</b></span>
                  <span>Hours: <b className="font-mono">{row.total_hours.toFixed(2)}</b></span>
                </div>
              </div>
              {row.employees.length === 0
                ? <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50">
                    No tip-eligible staff with approved hours at this branch on this date.
                  </div>
                : <table className="w-full text-sm">
                    <thead className="bg-white text-stone-700 border-b border-stone-100">
                      <tr>
                        <th className="text-left px-3 py-2">Employee</th>
                        <th className="text-right px-3 py-2">Hours</th>
                        <th className="text-right px-3 py-2">Portion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.employees.map(e => (
                        <tr key={e.employee_id} className="border-t border-stone-100">
                          <td className="px-3 py-2">{e.employee_name}</td>
                          <td className="px-3 py-2 text-right font-mono">{e.hours.toFixed(2)}</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${colorByValue(e.amount)}`}>
                            {fmtR(e.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          ))}
        </div>
      )}

      {report && report.rows?.length === 0
        && report.errors.length === 0
        && report.unmatched_branches.length === 0
        && (!report.diagnostics || report.diagnostics.upstream_rows_count === 0) && (
        <div className="bg-stone-50 border border-stone-200 rounded p-6 text-center text-stone-600">
          No reconciled tips in this range.
        </div>
      )}

      {/* Fix stragglers modal — manually assign a branch to records the audit-
          log workflow couldn't resolve. */}
      <Dialog open={stragglersOpen} onOpenChange={setStragglersOpen}>
        <DialogContent className="max-w-4xl" data-testid="stragglers-dialog">
          <DialogHeader>
            <DialogTitle>Assign branch to stragglers</DialogTitle>
            <p className="text-xs text-stone-500 pt-1">
              These approved attendance records in <b>{dateFrom} → {dateTo}</b> still have
              no branch — usually because TimeTec&apos;s audit log has no punch tied to that
              user on that date (retired device, deactivated user, or a gap in the audit
              log). Pick a branch and apply it to the selected records.
            </p>
          </DialogHeader>

          {stragglersLoading && (
            <div className="text-sm text-stone-500 py-8 text-center">Loading records…</div>
          )}

          {!stragglersLoading && stragglers.length === 0 && (
            <div className="text-sm text-stone-500 py-6 text-center">
              No missing-branch records in this range. 🎉
            </div>
          )}

          {!stragglersLoading && stragglers.length > 0 && (
            <>
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <Label className="text-xs">Assign branch</Label>
                  <Select value={bulkBranchId} onValueChange={setBulkBranchId}>
                    <SelectTrigger data-testid="stragglers-branch-select">
                      <SelectValue placeholder="Pick a branch…" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-stone-600 pb-2">
                  {Object.values(selectedStragglers).filter(Boolean).length}
                  {' '}of {stragglers.length} selected
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => {
                    const allOn = stragglers.every(r => selectedStragglers[r.id]);
                    const next = {};
                    stragglers.forEach(r => { next[r.id] = !allOn; });
                    setSelectedStragglers(next);
                  }}>
                  {stragglers.every(r => selectedStragglers[r.id]) ? 'Clear all' : 'Select all'}
                </Button>
              </div>

              <div className="overflow-y-auto max-h-[50vh] -mx-6 px-6 mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-700 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">In / Out</th>
                      <th className="text-right px-3 py-2">Hours</th>
                      <th className="text-left px-3 py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stragglers.map(r => (
                      <tr key={r.id} className="border-t border-stone-100 hover:bg-amber-50/40"
                        data-testid={`straggler-row-${r.id}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox"
                            checked={!!selectedStragglers[r.id]}
                            onChange={e => setSelectedStragglers(s => ({ ...s, [r.id]: e.target.checked }))}
                            data-testid={`straggler-check-${r.id}`} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
                        <td className="px-3 py-2">{r.name || '—'}</td>
                        <td className="px-3 py-2 text-xs text-stone-600">
                          {r.in_time || '—'} → {r.out_time || '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{r.work_hours || 0}</td>
                        <td className="px-3 py-2 text-xs text-stone-500">
                          {r.source || '—'}
                          {r.admin_override && ' · admin-override'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setStragglersOpen(false)}
              disabled={savingStragglers}
              data-testid="stragglers-cancel">Cancel</Button>
            <Button onClick={saveStragglers}
              disabled={savingStragglers || stragglers.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="stragglers-save">
              {savingStragglers ? 'Saving…' : `Assign to ${Object.values(selectedStragglers).filter(Boolean).length} record(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GratuitiesTab;
